import React, { useEffect, useState, useCallback } from 'react'
import { Container, Row, Col, Card, Form, Button, Spinner } from '@themesberg/react-bootstrap'
import { toast } from 'react-toastify'
import { useCookies } from 'react-cookie'
import { getSepayBanks, getSepaySetting, updateSepaySetting } from '../../../controller/api/setting'

const DEFAULT_CONFIG = {
  enabled: false,
  apiKey: '',
  banks: [],
}

const EMPTY_BANK = {
  bankAccountNumber: '',
  bankName: '',
  accountName: '',
  bankLogo: '',
}

const findMatchedBank = (banks, bankName) => {
  if (!bankName) return null

  const normalizedBankName = bankName.trim().toLowerCase()

  return (
    banks.find((bank) => bank.shortName?.trim().toLowerCase() === normalizedBankName) ||
    banks.find((bank) => bank.name?.trim().toLowerCase() === normalizedBankName) ||
    banks.find((bank) => bank.code?.trim().toLowerCase() === normalizedBankName) ||
    null
  )
}

const isBankComplete = (bank) =>
  Boolean(bank.bankName?.trim() && bank.bankAccountNumber?.trim() && bank.accountName?.trim())

const SepaySettings = () => {
  const [config, setConfig] = useState(DEFAULT_CONFIG)
  const [vietQrBanks, setVietQrBanks] = useState([])
  const [loading, setLoading] = useState(false)
  const [bankLoading, setBankLoading] = useState(false)
  const [saving, setSaving] = useState(false)
  const [allowManualBankInput, setAllowManualBankInput] = useState(false)

  const [cookies] = useCookies(['lottmekong'])
  const token = cookies.lottmekong

  const hydrateConfig = useCallback((incoming) => {
    setConfig({
      ...DEFAULT_CONFIG,
      ...(incoming || {}),
      banks: Array.isArray(incoming?.banks) ? incoming.banks : [],
    })
  }, [])

  const fetchSetting = useCallback(async () => {
    if (!token) {
      toast.error('Phiên đăng nhập không hợp lệ. Vui lòng đăng nhập lại.')
      return
    }
    setLoading(true)
    try {
      const data = await getSepaySetting(token)
      hydrateConfig(data)
    } catch (error) {
      console.error('Fetch SePay setting error', error)
      if (error.response?.status === 401) {
        toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.')
      } else {
        toast.error('Không thể tải cấu hình SePay. Vui lòng thử lại.')
      }
    } finally {
      setLoading(false)
    }
  }, [hydrateConfig, token])

  const fetchBanks = useCallback(async () => {
    if (!token) {
      return
    }

    setBankLoading(true)
    try {
      const bankList = await getSepayBanks(token)
      setVietQrBanks(bankList)
      setAllowManualBankInput(false)
    } catch (error) {
      console.error('Fetch VietQR bank list error', error)
      setVietQrBanks([])
      setAllowManualBankInput(true)
      toast.warn('Không tải được danh sách ngân hàng VietQR. Có thể nhập tay.')
    } finally {
      setBankLoading(false)
    }
  }, [token])

  useEffect(() => {
    fetchSetting()
  }, [fetchSetting])

  useEffect(() => {
    fetchBanks()
  }, [fetchBanks])

  const handleInputChange = (field) => (event) => {
    setConfig((prev) => ({
      ...prev,
      [field]: event.target.value,
    }))
  }

  const handleToggle = () => {
    setConfig((prev) => ({
      ...prev,
      enabled: !prev.enabled,
    }))
  }

  const updateBank = (index, changes) => {
    setConfig((prev) => ({
      ...prev,
      banks: prev.banks.map((bank, i) => (i === index ? { ...bank, ...changes } : bank)),
    }))
  }

  const handleBankFieldChange = (index, field) => (event) => {
    updateBank(index, { [field]: event.target.value })
  }

  const handleBankSelect = (index) => (event) => {
    const nextBankCode = event.target.value
    const bank = vietQrBanks.find((item) => item.code === nextBankCode)
    if (!bank) {
      updateBank(index, { bankName: '', bankLogo: '' })
      return
    }
    updateBank(index, {
      bankName: bank.shortName || bank.name || '',
      bankLogo: bank.logo || '',
    })
  }

  const addBank = () => {
    setConfig((prev) => ({
      ...prev,
      banks: [...prev.banks, { ...EMPTY_BANK }],
    }))
  }

  const removeBank = (index) => {
    setConfig((prev) => ({
      ...prev,
      banks: prev.banks.filter((_, i) => i !== index),
    }))
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (!token) {
      toast.error('Phiên đăng nhập không hợp lệ. Vui lòng đăng nhập lại.')
      return
    }

    if (config.enabled && !config.apiKey) {
      toast.warn('Vui lòng nhập API Key')
      return
    }
    if (config.enabled && !config.banks.some(isBankComplete)) {
      toast.warn('Vui lòng thêm ít nhất một ngân hàng đầy đủ thông tin')
      return
    }

    setSaving(true)
    try {
      const payload = {
        enabled: config.enabled,
        apiKey: config.apiKey,
        banks: config.banks
          .filter(isBankComplete)
          .map((bank) => ({
            bankAccountNumber: bank.bankAccountNumber,
            bankName: bank.bankName,
            accountName: bank.accountName,
            bankLogo: bank.bankLogo || '',
          })),
      }

      const updated = await updateSepaySetting(token, payload)
      hydrateConfig(updated)
      toast.success('Đã lưu cấu hình SePay thành công!')
    } catch (error) {
      console.error('Update SePay setting error', error)
      if (error.response?.status === 401) {
        toast.error('Phiên đăng nhập đã hết hạn. Vui lòng đăng nhập lại.')
      } else {
        toast.error('Không thể lưu cấu hình SePay. Vui lòng thử lại.')
      }
    } finally {
      setSaving(false)
    }
  }

  const renderBankRow = (bank, index) => {
    const selectedBankCode = allowManualBankInput
      ? ''
      : findMatchedBank(vietQrBanks, bank.bankName)?.code || ''

    return (
      <Card key={index} border="light" className="mb-3 bg-light">
        <Card.Body>
          <div className="d-flex justify-content-between align-items-center mb-3">
            <strong>Ngân hàng #{index + 1}</strong>
            <Button
              variant="outline-danger"
              size="sm"
              onClick={() => removeBank(index)}
              disabled={saving}
            >
              Xóa
            </Button>
          </div>

          {allowManualBankInput ? (
            <Form.Group className="mb-3">
              <Form.Label>Tên ngân hàng</Form.Label>
              <Form.Control
                type="text"
                placeholder="VD: MB Bank, Vietcombank, ..."
                value={bank.bankName}
                onChange={handleBankFieldChange(index, 'bankName')}
                disabled={loading || saving}
              />
              <Form.Text className="text-muted">
                Đang dùng nhập tay do chưa tải được danh sách ngân hàng từ VietQR.
              </Form.Text>
            </Form.Group>
          ) : (
            <Form.Group className="mb-3">
              <Form.Label>Ngân hàng</Form.Label>
              <Form.Select
                value={selectedBankCode}
                onChange={handleBankSelect(index)}
                disabled={loading || bankLoading || saving}
              >
                <option value="">{bankLoading ? 'Đang tải danh sách ngân hàng...' : 'Chọn ngân hàng từ VietQR'}</option>
                {vietQrBanks.map((item) => (
                  <option key={item.code} value={item.code}>
                    {item.shortName} ({item.code})
                  </option>
                ))}
              </Form.Select>
            </Form.Group>
          )}

          <Form.Group className="mb-3">
            <Form.Label>Số tài khoản</Form.Label>
            <Form.Control
              type="text"
              placeholder="Nhập số tài khoản ngân hàng"
              value={bank.bankAccountNumber}
              onChange={handleBankFieldChange(index, 'bankAccountNumber')}
              disabled={loading || saving}
            />
          </Form.Group>

          <Form.Group>
            <Form.Label>Tên chủ tài khoản</Form.Label>
            <Form.Control
              type="text"
              placeholder="Nhập tên chủ tài khoản"
              value={bank.accountName}
              onChange={handleBankFieldChange(index, 'accountName')}
              disabled={loading || saving}
            />
          </Form.Group>
        </Card.Body>
      </Card>
    )
  }

  return (
    <Container fluid className="py-4">
      <Row className="justify-content-center">
        <Col lg={8}>
          <Card>
            <Card.Header className="d-flex justify-content-between align-items-center">
              <div>
                <h4 className="mb-0">Cấu hình SePay</h4>
                <small className="text-muted">
                  Thiết lập webhook và thông tin ngân hàng cho thanh toán tự động qua SePay
                </small>
              </div>
              <Button variant="outline-secondary" size="sm" onClick={() => {
                fetchBanks()
                fetchSetting()
              }} disabled={loading || bankLoading || saving}>
                {loading ? <Spinner animation="border" size="sm" /> : 'Tải lại'}
              </Button>
            </Card.Header>
            <Card.Body>
              {loading ? (
                <div className="text-center py-5">
                  <Spinner animation="border" variant="primary" />
                  <p className="mt-3 text-muted">Đang tải cấu hình...</p>
                </div>
              ) : (
                <Form onSubmit={handleSubmit}>
                  <Form.Group className="mb-4">
                    <Form.Check
                      type="switch"
                      id="sepay-enabled"
                      label={config.enabled ? 'Đang bật' : 'Đang tắt'}
                      checked={config.enabled}
                      onChange={handleToggle}
                      disabled={saving}
                    />
                    <Form.Text className="text-muted">
                      Bật/tắt thanh toán qua SePay. Khi tắt, webhook sẽ không xử lý giao dịch.
                    </Form.Text>
                  </Form.Group>

                  <hr />

                  <Form.Group className="mb-4" controlId="apiKey">
                    <Form.Label>API Key</Form.Label>
                    <Form.Control
                      type="password"
                      placeholder="Nhập API Key từ SePay"
                      value={config.apiKey}
                      onChange={handleInputChange('apiKey')}
                      disabled={loading || saving}
                    />
                    <Form.Text className="text-muted">
                      Lấy từ trang quản lý SePay: Công ty &gt; Webhook &gt; API Key
                    </Form.Text>
                  </Form.Group>

                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <Form.Label className="mb-0">Danh sách ngân hàng</Form.Label>
                    <Button variant="outline-primary" size="sm" onClick={addBank} disabled={saving}>
                      + Thêm ngân hàng
                    </Button>
                  </div>

                  {config.banks.length === 0 ? (
                    <p className="text-muted">Chưa có ngân hàng nào. Bấm "Thêm ngân hàng" để thêm.</p>
                  ) : (
                    config.banks.map(renderBankRow)
                  )}

                  <div className="d-flex justify-content-end gap-2 mt-4">
                    <Button type="submit" variant="primary" disabled={saving}>
                      {saving ? (
                        <>
                          <Spinner animation="border" size="sm" className="me-2" />
                          Đang lưu...
                        </>
                      ) : (
                        'Lưu cấu hình'
                      )}
                    </Button>
                  </div>
                </Form>
              )}
            </Card.Body>
          </Card>

          <Card className="mt-4">
            <Card.Header>
              <h5 className="mb-0">Hướng dẫn cấu hình</h5>
            </Card.Header>
            <Card.Body>
              <ol className="mb-0">
                <li className="mb-2">Đăng ký tài khoản tại <strong>sepay.vn</strong> và liên kết ngân hàng</li>
                <li className="mb-2">Vào <strong>Công ty &gt; Webhook</strong>, tạo webhook mới</li>
                <li className="mb-2">Nhập URL webhook: <code>{window.location.origin.replace('admin', 'api')}/sepay/webhook</code></li>
                <li className="mb-2">Copy API Key từ SePay và dán vào ô API Key ở trên</li>
                <li className="mb-2">Thêm một hoặc nhiều ngân hàng (sẽ hiển thị cho người dùng khi nạp tiền)</li>
                <li className="mb-2">Bật công tắc để kích hoạt</li>
                <li>Người dùng chuyển khoản với nội dung <strong>NAP + số điện thoại</strong> (VD: NAP0901234567), hệ thống sẽ tự động cộng tiền</li>
              </ol>
            </Card.Body>
          </Card>
        </Col>
      </Row>
    </Container>
  )
}

export default SepaySettings
